import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from 'recharts';

const BAR_COLOR_DARK = 'var(--primitive-blue-500, #008EF5)';
const BAR_COLOR_MID = '#34A3F4';
const BAR_COLOR_LIGHT = '#66BFFF';

const BUCKETS = [
    { label: '0', min: 0, max: 16 },
    { label: '16', min: 16, max: 32 },
    { label: '32', min: 32, max: 48 },
    { label: '48', min: 48, max: 64 },
    { label: '64', min: 64, max: 80 },
    { label: '80', min: 80, max: 96 },
    { label: '96', min: 96, max: 112 },
    { label: '112', min: 112, max: 128 },
    { label: '128+', min: 128, max: Infinity },
];

function barColor(count, maxCount) {
    if (count === maxCount && maxCount > 0) return BAR_COLOR_DARK;
    const ratio = maxCount > 0 ? count / maxCount : 0;
    return ratio > 0.5 ? BAR_COLOR_MID : BAR_COLOR_LIGHT;
}

function toBucketLabel(bucket) {
    if (bucket.label === '128+') return '128+';
    return `${bucket.min}-${bucket.max}`;
}

/**
 * Input Size distribution card: median/max token counts + histogram.
 * Always renders 9 bars (0–16, 17–32, … 113–128, 128+).
 */
const InputSizeCard = ({ inputSize }) => {
    const { medianTokens = 0, maxTokens = 0, histogram = [] } = inputSize || {};

    const chartData = useMemo(() => {
        const bucketMap = {};
        BUCKETS.forEach((b) => { bucketMap[b.label] = 0; });
        histogram.forEach((h) => {
            if (bucketMap[h.bucket] !== undefined) {
                bucketMap[h.bucket] = h.count;
            }
        });
        return BUCKETS.map((b) => ({ bucket: b.label, count: bucketMap[b.label] }));
    }, [histogram]);

    const maxCount = Math.max(...chartData.map((h) => h.count), 0);

    return (
        <div className="metric-card">
            <div className="input-size-card__top">
                <div className="input-size-card__info">
                    <div className="metric-card__label">Input size</div>
                    <div className="input-size-card__subtitle">
                        Tokens per request - longer inputs drive the latency tail
                    </div>
                </div>
                <div className="input-size-card__stats">
                    <div className="input-size-card__stat-row">
                        <span className="input-size-card__stat-value">{medianTokens}</span>
                        <span className="input-size-card__stat-label">Median</span>
                    </div>
                    <div className="input-size-card__stat-row">
                        <span className="input-size-card__stat-value">{maxTokens}</span>
                        <span className="input-size-card__stat-label">Max</span>
                    </div>
                </div>
            </div>

            <div className="input-size-card__chart">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 4, right: 4, left: 4, bottom: 0 }} barCategoryGap="4%">
                        <XAxis
                            dataKey="bucket"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 11 }}
                            ticks={['0', '32', '64', '96', '128+']}
                        />
                        <YAxis hide />
                        <RechartsTooltip
                            cursor={false}
                            content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const d = payload[0].payload;
                                const b = BUCKETS.find((x) => x.label === d.bucket);
                                const label = b ? toBucketLabel(b) : d.bucket;
                                return (
                                    <div style={{ background: '#fff', border: '1px solid #E2E2E9', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}>
                                        {d.count} requests
                                        <br />
                                        {label} tokens
                                    </div>
                                );
                            }}
                        />
                        <Bar dataKey="count" radius={[4, 4, 4, 4]} isAnimationActive={false}>
                            {chartData.map((entry, i) => (
                                <Cell key={i} fill={barColor(entry.count, maxCount)} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default InputSizeCard;
